/**
 * Every public page, and the head it publishes.
 *
 * This is the screen the brief calls "the feature Webflow lacked". Webflow
 * binds a collection template's SEO to a field, so every Provider page gets the
 * same shape of title and no single page can be given its own; and there was
 * never one place to see all 200 pages' titles side by side, which is how
 * duplicates and empty descriptions survive for years.
 *
 * Two kinds of row, edited in two places, which is not an implementation detail
 * the clinic should have to care about:
 *
 *   - a **static page** is edited here, writing `seo.*` into
 *     `content/pages.json`;
 *   - an **item page** is edited on the item itself, because that is where its
 *     words are — the row links there rather than making a second place to type
 *     the same thing.
 *
 * Every warning here is a warning. A 62-character title that says the right
 * thing beats a 58-character title that does not, and a panel that refuses the
 * first has stopped being useful.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ApiError, api } from "../api.js";
import { Message, Meter, SearchPreview } from "../components/bits.jsx";
import { go } from "../app.jsx";

const TITLE_LIMIT = 60;
const DESCRIPTION_LIMIT = 160;

export default function Seo({ may }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [only, setOnly] = useState("all");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      setRows((await api.get("seo")).rows);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not load the pages.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => {
    if (!rows) return [];
    const needle = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (only === "problems" && !problems(row).length) return false;
      if (only === "overridden" && !row.overridden) return false;
      if (only === "pages" && row.kind !== "page") return false;
      if (!needle) return true;
      return `${row.url} ${row.title} ${row.description}`.toLowerCase().includes(needle);
    });
  }, [rows, query, only]);

  const counts = useMemo(() => {
    if (!rows) return null;
    return {
      total: rows.length,
      problems: rows.filter((r) => problems(r).length).length,
      overridden: rows.filter((r) => r.overridden).length,
      hidden: rows.filter((r) => r.noindex).length,
    };
  }, [rows]);

  return (
    <>
      <div className="head">
        <div>
          <h1>Search</h1>
          <p className="sub">
            What every page says in a search result. {counts && `${counts.total} pages, ${counts.problems} worth a look.`}
          </p>
        </div>
      </div>

      <Message level="error">{error}</Message>

      <div className="card">
        <header className="row">
          <input
            style={{ flex: "1 1 16rem" }}
            placeholder="Search addresses and titles"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search pages"
          />
          <select value={only} onChange={(e) => setOnly(e.target.value)} style={{ width: "auto" }} aria-label="Filter">
            <option value="all">Every page</option>
            <option value="problems">Worth a look</option>
            <option value="overridden">Has an override</option>
            <option value="pages">Static pages only</option>
          </select>
        </header>

        <div className="body" style={{ padding: 0 }}>
          {!rows ? (
            <div className="empty">Loading…</div>
          ) : !shown.length ? (
            <div className="empty">Nothing matches.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Address</th>
                  <th>Title</th>
                  <th style={{ width: "5rem" }} />
                  <th>Description</th>
                  <th style={{ width: "5rem" }} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => {
                  const found = problems(row);
                  return (
                    <tr key={row.key}>
                      <td className="mono truncate" style={{ maxWidth: "18ch" }}>
                        {row.url}
                        {row.noindex && (
                          <>
                            {" "}
                            <span className="pill noindex">off Google</span>
                          </>
                        )}
                      </td>
                      <td className="truncate" style={{ maxWidth: "28ch" }}>
                        {row.title || <em className="hint">none</em>}
                        {row.overridden && <span className="hint"> · own</span>}
                      </td>
                      <td>
                        <Meter value={row.title} limit={TITLE_LIMIT} />
                      </td>
                      <td className="truncate" style={{ maxWidth: "30ch" }}>
                        {row.description || <em className="hint">none</em>}
                      </td>
                      <td>
                        <Meter value={row.description} limit={DESCRIPTION_LIMIT} />
                      </td>
                      <td>
                        {row.kind === "page" ? (
                          may("seo") && (
                            <button className="quiet" onClick={() => setEditing(row)}>
                              Edit
                            </button>
                          )
                        ) : (
                          <button className="quiet" onClick={() => go(`${row.kind}/${row.key.split("/")[1]}`)}>
                            Open
                          </button>
                        )}
                        {found.length > 0 && (
                          <div className="hint" style={{ color: "var(--warn)" }}>
                            {found.join(" · ")}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {editing && <PageEditor row={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
}

/** What is worth somebody's attention on this row. Never a refusal. */
function problems(row) {
  const found = [];
  if (!String(row.description || "").trim()) found.push("no description");
  else if (row.description.length > DESCRIPTION_LIMIT) found.push("long description");
  if (!String(row.title || "").trim()) found.push("no title");
  else if (row.title.length > TITLE_LIMIT) found.push("long title");
  if (row.duplicate) found.push("duplicate title");
  return found;
}

/** Editing a static page's head, which lives in content/pages.json. */
function PageEditor({ row, onClose, onSaved }) {
  const [draft, setDraft] = useState({
    title: row.title || "",
    description: row.description || "",
    canonical: row.canonical || "",
    ogImage: row.ogImage || "",
    noindex: row.noindex,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.put("seo", { url: row.url, ...draft });
      onSaved();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not save.");
      setBusy(false);
    }
  }

  const field = (key, label, { limit = 0, textarea = false } = {}) => (
    <div className="field">
      <div className="spread">
        <label htmlFor={`seo-${key}`}>{label}</label>
        {limit && <Meter value={draft[key]} limit={limit} />}
      </div>
      {textarea ? (
        <textarea id={`seo-${key}`} rows={3} value={draft[key]} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} />
      ) : (
        <input id={`seo-${key}`} value={draft[key]} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} />
      )}
    </div>
  );

  return (
    <div className="card">
      <header className="spread">
        <div>
          <h2>
            <span className="mono">{row.url}</span>
          </h2>
          <p className="help">Changing these changes how this page appears in search results.</p>
        </div>
        <button className="quiet" onClick={onClose}>
          Close
        </button>
      </header>
      <div className="body">
        {field("title", "Title", { limit: TITLE_LIMIT })}
        {field("description", "Description", { limit: DESCRIPTION_LIMIT, textarea: true })}
        <SearchPreview url={row.url} title={draft.title} description={draft.description} />

        <div style={{ marginTop: "1rem" }}>
          {field("ogImage", "Sharing picture")}
          {field("canonical", "Canonical address")}
          <div className="field">
            <div className="switch">
              <input
                id="seo-noindex"
                type="checkbox"
                checked={draft.noindex}
                onChange={(e) => setDraft({ ...draft, noindex: e.target.checked })}
              />
              <label htmlFor="seo-noindex" style={{ marginBottom: 0 }}>
                Keep this page off Google
              </label>
            </div>
            <p className="help">The page stays published and linked. It leaves sitemap.xml and asks search engines to skip it.</p>
          </div>
        </div>

        <Message level="error">{error}</Message>
        <div className="row" style={{ marginTop: "0.5rem" }}>
          <button className="primary" onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
          <button className="quiet" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
